// Helper to format numbers with sign and dollar sign
export const formatPnL = (val, decimals = 2) => {
  if (val === undefined || val === null) return '-';
  const sign = val >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(val).toFixed(decimals)}`;
};

/** Format funding/payment amounts: 4 decimals for small amounts (e.g. 0.0017, -0.0082). */
export const formatFundingAmount = (val) => {
  if (val == null || Number.isNaN(Number(val))) return '–';
  const n = Number(val);
  if (Math.abs(n) < 0.01) return n.toFixed(4);
  return n.toFixed(2);
};

/** Same as formatFundingAmount but with +/- and $ (e.g. -$0.000205). */
export const formatFundingPnL = (val) => {
  if (val == null || Number.isNaN(Number(val))) return '-';
  const sign = val >= 0 ? '+' : '-';
  return `${sign}$${formatFundingAmount(Math.abs(val))}`;
};

const MIN_DAYS = 1 / 24; // 1 hour, to avoid huge annualized % for very recent positions

/**
 * Funding % since position open: period % and annualized %.
 * periodPct = (netFunding / notional) * 100
 * annualizedPct = periodPct * (365 / days)
 * @param {number} netFunding - Cumulative net funding (e.g. from position or by_market)
 * @param {number} notional - Position notional (size * mark_price or position.notional)
 * @param {Date|string|number} openingTimeOrDays - Position opening time (Date or ISO string) or days since open (number)
 * @returns {{ periodPct: number, annualizedPct: number } | null}
 */
export function getFundingPct(netFunding, notional, openingTimeOrDays) {
  if (notional == null || notional <= 0) return null;
  if (netFunding == null || Number.isNaN(Number(netFunding))) return null;
  const periodPct = (Number(netFunding) / notional) * 100;
  let days = 1;
  if (typeof openingTimeOrDays === 'number' && openingTimeOrDays > 0) {
    days = Math.max(MIN_DAYS, openingTimeOrDays);
  } else if (openingTimeOrDays != null) {
    const openMs = new Date(openingTimeOrDays).getTime();
    if (!Number.isNaN(openMs)) {
      days = (Date.now() - openMs) / (1000 * 60 * 60 * 24);
      days = Math.max(MIN_DAYS, days);
    }
  }
  const annualizedPct = periodPct * (365 / days);
  return { periodPct, annualizedPct };
}

// Helper for total exchange/instrument stats
export const getExtendedStats = (positions) => {
  if (!positions || positions.length === 0) return null;
  
  let totalUnrealizedPnl = 0;
  let totalFunding = 0;
  let totalMargin = 0;
  let totalNotional = 0;
  
  positions.forEach(p => {
    totalUnrealizedPnl += (p.unrealized_pnl || 0);
    totalFunding += (p.cumulative_funding_payout || 0);
    totalMargin += (p.margin_used || 0);
    totalNotional += (p.notional || (p.size * p.mark_price) || 0);
  });
  
  const totalPnL = totalUnrealizedPnl + totalFunding;
  
  // Find average age of positions
  const now = new Date();
  let totalDays = 0;
  let positionsWithTime = 0;
  
  positions.forEach(p => {
    if (p.opening_time) {
      const openDate = new Date(p.opening_time);
      const days = (now - openDate) / (1000 * 60 * 60 * 24);
      totalDays += Math.max(0.01, days); // Avoid division by zero
      positionsWithTime++;
    }
  });
  
  const avgDays = positionsWithTime > 0 ? totalDays / positionsWithTime : 1;
  
  // Funding % for period (on notional)
  const fundingPercentPeriod = totalNotional > 0 ? (totalFunding / totalNotional) * 100 : 0;
  const fundingPercentYearly = fundingPercentPeriod * (365 / avgDays);
  
  return {
    totalUnrealizedPnl,
    totalFunding,
    totalPnL,
    totalMargin,
    totalNotional,
    fundingPercentPeriod,
    fundingPercentYearly,
    avgDays
  };
};

